import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { to: "/", label: "Index", testid: "nav-home-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/projects", label: "Projects", testid: "nav-projects-link" },
  { to: "/careers", label: "Careers", testid: "nav-careers-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Logo = () => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
    <img
      src="/brand/eagle-mark.png"
      alt="APRASIM eagle logo"
      className="h-9 w-auto transition-transform duration-300 group-hover:scale-110"
    />
    <div className="hidden sm:flex flex-col leading-none">
      <span className="font-display text-base uppercase tracking-tight">APRASIM</span>
      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-600">Contracting Corp.</span>
    </div>
  </Link>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const loc = useLocation();
  const lastPath = useRef(loc.pathname);

  useEffect(() => {
    if (lastPath.current !== loc.pathname) {
      lastPath.current = loc.pathname;
      setOpen(false);
    }
  }, [loc.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b-2 border-black transition-colors duration-300 ${
        scrolled ? "bg-[#F0F0EC]/95 backdrop-blur-xl" : "bg-[#F0F0EC]/80 backdrop-blur-md"
      }`}
    >
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="absolute bottom-[-2px] left-0 right-0 h-[3px] bg-[#FF4F00] origin-left z-[60]"
      />
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.testid}
              className={({ isActive }) =>
                `font-mono text-xs uppercase tracking-[0.2em] link-underline ${isActive ? "is-active text-[#FF4F00]" : "text-black"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="nav-cta-quote"
            className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#FF4F00] hover:text-black transition-colors duration-300"
          >
            Request Bid
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <button
          aria-label="Toggle menu"
          data-testid="nav-menu-toggle"
          className="lg:hidden w-10 h-10 brut-border flex items-center justify-center"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden border-t-2 border-black bg-[#F0F0EC] overflow-hidden"
          >
            <div className="flex flex-col">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `block px-5 py-4 border-b border-black/20 font-mono text-xs uppercase tracking-[0.2em] ${isActive ? "text-[#FF4F00]" : "text-black"}`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-cta-quote"
                className="m-5 inline-flex items-center justify-center gap-2 bg-black text-white px-4 py-3 font-mono text-xs uppercase tracking-[0.2em]"
              >
                Request Bid <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="bg-black text-[#F0F0EC] mt-24 overflow-hidden">
    <div className="border-t border-b border-[#FF4F00]/40 stripes h-3 opacity-80" />
    <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-5">
        <div className="flex items-center gap-3">
          <img src="/brand/eagle-mark.png" alt="APRASIM eagle logo" className="h-12 w-auto" />
          <div>
            <div className="font-display text-2xl uppercase tracking-tight">APRASIM Contracting Corp.</div>
            <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-neutral-400">Heavy Civil Construction</div>
          </div>
        </div>
        <p className="mt-6 max-w-md text-sm leading-relaxed text-neutral-300">
          A Lower Mainland heavy civil contractor led by Owner/President Amaninder Singh — an engineer-led team backed by 70+ years of combined heavy civil construction experience.</p>
      </div>
      <div className="md:col-span-2">
        <div className="label-mono text-neutral-400 mb-4">Pages</div>
        <ul className="space-y-2 font-mono text-xs">
          {NAV_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} data-testid={`footer-${l.testid}`} className="hover:text-[#FF4F00] uppercase tracking-[0.2em] transition-colors duration-200">
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-3">
        <div className="label-mono text-neutral-400 mb-4">Office</div>
        <p className="font-mono text-xs leading-7 text-neutral-200">
          104-8645 Young Rd<br />
          Chilliwack, BC<br />
          British Columbia, Canada
        </p>
      </div>
      <div className="md:col-span-2">
        <div className="label-mono text-neutral-400 mb-4">Contact</div>
        <ul className="space-y-3 font-mono text-xs text-neutral-200">
          <li>+1 (778) 228-3264</li>
          <li>info@aprasim.ca</li>
          <li>estimating@aprasim.ca</li>
        </ul>
      </div>
    </div>
    <div className="max-w-[1500px] mx-auto px-5 sm:px-8 select-none pointer-events-none" aria-hidden="true">
      <div className="font-display uppercase whitespace-nowrap text-[13.5vw] leading-[0.8] text-outline-light -mb-[3vw]">
        APRASIM
      </div>
    </div>
    <div className="border-t border-neutral-800 relative bg-black">
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          © {new Date().getFullYear()} APRASIM Contracting Corp. All rights reserved.
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
          Built on British Columbia
        </span>
      </div>
    </div>
  </footer>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-[#F0F0EC] text-black">
      <div className="grain-overlay" />
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
